import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CustomEnumValue } from '../../domain/value-object/CustomEnumValue'
import { WbsItemTypeVO } from '../../domain/value-object/WbsItemTypeVO'
import { FunctionProperty, PropertyType } from '../../lib/commons/appFunction'
import { CUSTOM_ENUM_NONE } from '../../lib/commons/customEnum'
import { parse } from '../../lib/commons/i18nLabel'
import {
  CustomEnumCode,
  getCustomEnumValues,
} from '../../lib/functions/customEnumValue'
import projectMember, {
  ProjectMemberDetail,
} from '../../lib/functions/projectMember'
import sprint, { SprintDetail, SprintStatus } from '../../lib/functions/sprint'
import { fetchTags } from '../../lib/functions/tag'
import team, { TeamProps } from '../../lib/functions/team'
import store from '../../store'
import { receivedTags } from '../../store/tag'
import repositories from '../containers/meta/repositories'

export type WbsItemSelectColumnOptionType =
  | {
      [key: string]:
        | CustomEnumValue[]
        | TeamProps[]
        | ProjectMemberDetail[]
        | SprintDetail[]
        | WbsItemTypeVO[]
    }
  | undefined

export type WbsItemColumnOptionType =
  | 'STATUS'
  | 'SUBSTATUS'
  | 'PRIORITY'
  | 'DIFFICULTY'
  | 'TEAM'
  | 'MEMBER'
  | 'SPRINT'
  | 'TAG'

export const useWbsItemSelectColumnOptions = ({
  projectUuid,
  targetColumnOptionTypes = [
    'STATUS',
    'SUBSTATUS',
    'PRIORITY',
    'DIFFICULTY',
    'TEAM',
    'MEMBER',
    'SPRINT',
    'TAG',
  ],
}: {
  projectUuid: string
  targetColumnOptionTypes?: WbsItemColumnOptionType[]
}) => {
  const [selectColumnOptions, setSelectColumnOptions] =
    useState<WbsItemSelectColumnOptionType>(undefined)
  const [fetchedSelectColumnOptions, setFetchedSelectColumnOptions] =
    useState<boolean>(false)

  const fetchStatusOptions = useCallback(async projectUuid => {
    const response = await getCustomEnumValues({
      customEnumCode: CustomEnumCode.WBS_STATUS,
      groupUuid: projectUuid,
    })
    return response.json.map(v => ({ ...v, nameI18n: parse(v.nameI18n) }))
  }, [])

  const fetchSubstatusOptions = useCallback(async projectUuid => {
    const response = await getCustomEnumValues({
      customEnumCode: CustomEnumCode.WBS_SUBSTATUS,
      groupUuid: projectUuid,
    })
    return response.json.map(v => ({ ...v, nameI18n: parse(v.nameI18n) }))
  }, [])

  const fetchPriorityOptions = useCallback(async projectUuid => {
    const response = await getCustomEnumValues({
      customEnumCode: CustomEnumCode.WBS_PRIORITY,
      groupUuid: projectUuid,
    })
    return response.json.map(v => ({ ...v, nameI18n: parse(v.nameI18n) }))
  }, [])

  const fetchDifficultyOptions = useCallback(async projectUuid => {
    const response = await getCustomEnumValues({
      customEnumCode: CustomEnumCode.WBS_DIFFICULTY,
      groupUuid: projectUuid,
    })
    return response.json.map(v => ({ ...v, nameI18n: parse(v.nameI18n) }))
  }, [])

  const fetchTeamOptions = useCallback(async (projectUuid: string) => {
    const response = await team.getTeams(projectUuid)
    return response.json
  }, [])

  const fetchMemberOptions = useCallback(async () => {
    const response = await projectMember.getProjectMembers()
    return response.json.map(v => v.user)
  }, [])

  const fetchSprintOptions = useCallback(async () => {
    const response = await sprint.getSprints({
      statusList: [
        SprintStatus.STANDBY,
        SprintStatus.INPROGRESS,
        SprintStatus.FINISHED,
        SprintStatus.CANCELED,
      ],
    })
    return response.json
  }, [])

  const fetchTagOptions = useCallback(async () => {
    const response = await fetchTags({ projectUuid: undefined })
    return response.json
  }, [])

  const targetOptionTypeStr = useMemo(
    () => targetColumnOptionTypes.toString(),
    [targetColumnOptionTypes]
  )

  const dispatch = useDispatch()
  const fetchSelectColumnOptions = useCallback(
    async () => {
      const [
        status,
        substatus,
        priority,
        difficulty,
        team,
        member,
        sprint,
        tag,
      ] = await Promise.all([
        targetColumnOptionTypes.includes('STATUS')
          ? fetchStatusOptions(projectUuid)
          : undefined,
        targetColumnOptionTypes.includes('SUBSTATUS')
          ? fetchSubstatusOptions(projectUuid)
          : undefined,
        targetColumnOptionTypes.includes('PRIORITY')
          ? fetchPriorityOptions(projectUuid)
          : undefined,
        targetColumnOptionTypes.includes('DIFFICULTY')
          ? fetchDifficultyOptions(projectUuid)
          : undefined,
        targetColumnOptionTypes.includes('TEAM')
          ? fetchTeamOptions(projectUuid)
          : undefined,
        targetColumnOptionTypes.includes('MEMBER')
          ? fetchMemberOptions()
          : undefined,
        targetColumnOptionTypes.includes('SPRINT')
          ? fetchSprintOptions()
          : undefined,
        targetColumnOptionTypes.includes('TAG') ? fetchTagOptions() : undefined,
      ])
      const projectState = store.getState().project
      const wbsItemType = [
        ...projectState.wbsItemTypes.getAll(),
        ...projectState.ticketListTypes,
        ...projectState.ticketTypes,
      ]
      setSelectColumnOptions({
        wbsItemType,
        status,
        substatus,
        priority,
        difficulty,
        team,
        member,
        sprint,
        tag,
      })
      if (targetColumnOptionTypes.includes('TAG')) {
        dispatch(receivedTags(projectUuid, tag))
      }
      setFetchedSelectColumnOptions(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      projectUuid,
      targetOptionTypeStr,
      dispatch,
      fetchStatusOptions,
      fetchSubstatusOptions,
      fetchPriorityOptions,
      fetchDifficultyOptions,
      fetchTeamOptions,
      fetchMemberOptions,
      fetchSprintOptions,
      fetchTagOptions,
    ]
  )

  useEffect(() => {
    if (!projectUuid) return
    fetchSelectColumnOptions()
  }, [fetchSelectColumnOptions, projectUuid])

  const result = useMemo(
    () => ({
      selectColumnOptions,
      fetchedSelectColumnOptions,
    }),
    [selectColumnOptions, fetchedSelectColumnOptions]
  )
  return result
}
